import { PIN_LEVEL_BADGES, MEMBERSHIP_BADGES } from "./components/CircleBadge";
import { normalizeTags } from "shared/api/products";
import { formatShortDate } from "shared/@utils/formatDate";
import { sortSizes } from "./sizeOrder";

/**
 * Shared product-catalog helpers used by both the Store list page and the
 * Order Management → Create Order catalog so they render the exact same
 * products from the same mock-backed API (`shared/api` products namespace).
 */

export const filterGroups = [
  {
    title: "Filter by Source",
    key: "type",
    options: [
      { value: "WWG", label: "WWG" },
      { value: "3rd", label: "3rd Party" },
    ],
  },
  {
    title: "Filter by Type",
    key: "productType",
    // Overridden at runtime by the live product-type universe
    // (ProductCatalogBrowser). Kept only as a fallback for offline/mock use.
    options: ["Literature", "Clothing", "Drinkware", "Accessories"],
  },
  {
    title: "Filter by Category",
    key: "category",
    // Overridden at runtime by the live WWG Store App Categories tree
    // (ProductCatalogBrowser). Kept only as a fallback for offline/mock use.
    options: ["Literature", "Clothing", "Drinkware", "Accessories"],
  },
  {
    title: "Filter by Language",
    key: "language",
    options: ["English", "Spanish", "Korean"],
  },
  {
    title: "Filter by Tag",
    key: "tag",
    clearable: true,
    options: [
      "Classics",
      "Leadership Summit",
      "Family Reunion",
      "Summer Conference",
      "Free Enterprise Days",
      "Event",
      "Maxwell",
      "Tsika",
      "Lifestyle",
      "English",
      "Travel",
      "Bundle",
      "Discount",
    ],
  },
  {
    title: "Filter by Status",
    key: "status",
    options: ["Published", "Scheduled", "In Review", "Archived"],
  },
  {
    title: "Filter by Membership",
    key: "membership",
    options: [
      { value: "C", label: "Complimentary (C)" },
      { value: "P", label: "Premier (P)" },
    ],
  },
  {
    title: "Filter by Pin Level",
    key: "pinLevel",
    options: [
      { value: "IBO", label: "IBO" },
      { value: "PLA", label: "Platinum (PLA)" },
      { value: "EME", label: "Emerald (EME)" },
      { value: "DIA", label: "Diamond (DIA)" },
    ],
  },
  {
    title: "Filter by Availability",
    key: "availability",
    options: [
      { value: "preorder", label: "Pre-Order" },
      { value: "available", label: "Available" },
      { value: "outofstock", label: "Out of Stock" },
      // "Discontinued" retired (BugHerd #73 in the editor, #91 here): a
      // product we stop selling is Archived instead.
    ],
  },
];

export const pinLevelLookup = {
  IBO: PIN_LEVEL_BADGES.IBO,
  PLA: PIN_LEVEL_BADGES.PLA,
  EME: PIN_LEVEL_BADGES.EME,
  EM: PIN_LEVEL_BADGES.EM,
  DIA: PIN_LEVEL_BADGES.DIA,
};

export const membershipLookup = {
  C: MEMBERSHIP_BADGES.C,
  P: MEMBERSHIP_BADGES.P,
};

// All membership / pin-level codes. The catalog filters for these are
// inclusion checkboxes that default to all-on (everything shows). Unchecking a
// code hides products available to that audience, so these defaults must seed
// the initial filter state on both catalog pages.
export const ALL_MEMBERSHIP_CODES = ["C", "P"];
export const ALL_PIN_CODES = ["IBO", "PLA", "EME", "DIA"];

// Canonical default match modes for the remaining multi-value filter (tags).
// Membership and pin-level filters are inclusion checkboxes (no match mode).
export const DEFAULT_FILTER_MODES = {
  tag: "AND",
};

// "draft" is kept here only so a product saved before the status was retired
// still renders a label instead of a blank cell. Nothing writes it any more.
export const STATUS_LABELS = {
  archived: "Archived",
  in_review: "In Review",
  scheduled: "Scheduled",
  published: "Published",
  draft: "In Review",
};

// "Draft" was retired as a publish status (BugHerd #51/#54/#59), but it used to
// sit in the Store's default status filter, so it is baked into every bookmarked
// `?f=` URL made before the change. The filter rail no longer offers a Draft
// checkbox, so a leftover value would narrow the list on something the user can
// neither see nor clear — and a saved filter of Draft alone would show an
// unexplained empty catalog. Strip it on read; if that empties the status group,
// fall back to the caller's defaults.
export function dropRetiredStatuses(filters, initialFilters) {
  if (!Array.isArray(filters?.status) || !filters.status.includes("Draft")) {
    return filters;
  }
  const status = filters.status.filter((s) => s !== "Draft");
  return {
    ...filters,
    status: status.length ? status : initialFilters?.status || [],
  };
}

export const centsToDollars = (cents) =>
  typeof cents === "number" ? cents / 100 : undefined;
const normalizeStatusKey = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

/**
 * Map a canonical Product (as returned by `api.products.list`) to the shape
 * `ProductItemCard` expects. When the real backend lands this mapper is the
 * only thing that needs to change.
 */
export const toListItem = (p) => {
  const statusKey = normalizeStatusKey(p.status);
  // Prices are NetSuite-owned and read live from SQL; `effectivePricing` is that
  // base with any admin override applied. `pricing` is the override store alone,
  // so it is only a fallback for responses that predate the SQL read-through.
  const price = p.effectivePricing || p.pricing;
  // Imagery follows the same rule as pricing. `effectiveThumbnailUrl` is the
  // NetSuite asset (Store.Assets, chosen exactly as the storefront chooses it)
  // with any admin upload applied on top. `image`/`thumbnailUrl` are the raw
  // override fields, kept only as a fallback for responses that predate the SQL
  // read-through.
  const image =
    p.effectiveThumbnailUrl ||
    p.image ||
    p.thumbnailUrl?.url ||
    p.thumbnailUrl ||
    null;
  return {
    id: p.id,
    image,
    name: p.name || p.netsuiteTitle,
    date: formatShortDate(p.updatedAt),
    retailPrice: centsToDollars(price?.retailCents) ?? p.retailPrice,
    pm: centsToDollars(price?.premierMemberCents) ?? p.pm,
    sku: p.sku || p.netsuiteSku,
    productType: p.productType || p.category,
    status: p.status,
    statusLabel: STATUS_LABELS[statusKey] || p.statusLabel || p.status,
    pinLevels: (p.audiencePinCodes || []).map(
      (code) => pinLevelLookup[code] || { label: code }
    ),
    memberships: (p.audienceMembershipCodes || []).map(
      (code) => membershipLookup[code] || { label: code }
    ),
    tags: normalizeTags(p.tags || []),
    author: p.author,
    unitsSold: typeof p.unitsSold === "number" ? p.unitsSold : undefined,
    isBundle: Boolean(p.isBundle),
    bundledProducts: Array.isArray(p.bundledProducts) ? p.bundledProducts : [],
    variations: Array.isArray(p.variations) ? p.variations : [],
    audiencePinCodes: p.audiencePinCodes || [],
    audienceMembershipCodes: p.audienceMembershipCodes || [],
    variationValue: p.variations?.length ? `${p.variations.length}` : undefined,
    variationLabel: "Variation Options",
    size: p.variations?.length
      ? sortSizes(
          p.variations
            .flatMap((v) => v.sizes || [])
            .filter((s, i, arr) => arr.indexOf(s) === i)
        ).join(" ") || undefined
      : undefined,
  };
};
